import React, { useState, useEffect } from 'react';
import './InstallPWA.css';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Debug: Check installability
    const checkInstallability = async () => {
      const info = {
        hasServiceWorker: 'serviceWorker' in navigator,
        isStandalone: window.matchMedia('(display-mode: standalone)').matches,
        manifestExists: false,
        iconsExist: { icon192: false, icon512: false }
      };

      // Check manifest
      try {
        const manifestResponse = await fetch('/manifest.json');
        if (manifestResponse.ok) {
          info.manifestExists = true;
          const manifest = await manifestResponse.json();
          
          // Check icons
          for (const icon of manifest.icons || []) {
            if (icon.sizes === '192x192') {
              try {
                const iconResponse = await fetch(icon.src);
                info.iconsExist.icon192 = iconResponse.ok;
              } catch (e) {
                // Icon 192 not found
              }
            }
            if (icon.sizes === '512x512') {
              try {
                const iconResponse = await fetch(icon.src);
                info.iconsExist.icon512 = iconResponse.ok;
              } catch (e) {
                // Icon 512 not found
              }
            }
          }
        }
      } catch (e) {
        // Manifest check failed
      }

      setDebugInfo(info);
    };

    checkInstallability();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if already installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallButton(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    await deferredPrompt.userChoice;

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  // Manual install instructions (fallback)
  const showManualInstructions = debugInfo && (!debugInfo.iconsExist.icon192 || !debugInfo.iconsExist.icon512);

  // Don't show if already installed
  if (isInstalled) {
    return null;
  }

  // Show manual instructions if icons are missing
  if (showManualInstructions) {
    return (
      <div className="install-pwa-banner install-pwa-warning">
        <div className="install-pwa-content">
          <div className="install-pwa-text">
            <span className="install-pwa-icon">⚠️</span>
            <div>
              <strong>Iconos faltantes para PWA</strong>
              <p>
                Genera los iconos en{' '}
                <a href="/create-icons-simple.html" target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary)', textDecoration: 'underline'}}>
                  /create-icons-simple.html
                </a>
              </p>
            </div>
          </div>
          <button
            className="install-pwa-close"
            onClick={() => setShowInstallButton(false)}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  // Don't show if prompt not available
  if (!showInstallButton || !deferredPrompt) {
    return null;
  }

  return (
    <div className="install-pwa-banner">
      <div className="install-pwa-content">
        <div className="install-pwa-text">
          <span className="install-pwa-icon">📱</span>
          <div>
            <strong>Instala nuestra app</strong>
            <p>Acceso rápido desde tu pantalla de inicio</p>
          </div>
        </div>
        <button 
          className="btn btn-primary install-pwa-btn"
          onClick={handleInstallClick}
          aria-label="Instalar aplicación"
        >
          Instalar
        </button>
        <button
          className="install-pwa-close"
          onClick={() => setShowInstallButton(false)}
          aria-label="Cerrar"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default InstallPWA;
